import { Response } from 'lib-shared-legacy-types';

import { AutomationNodeCloseT } from 'components/automations/components/nodeTypes/close';
import { AutomationNodeConditionT } from 'components/automations/components/nodeTypes/condition';
import type { AutomationNodeInitT } from 'components/automations/components/nodeTypes/init';
import type { AutomationNodeQueueT } from 'components/automations/components/nodeTypes/queue';
import { AutomationNodeReplyT } from 'components/automations/components/nodeTypes/reply';
import { AutomationNodeStopT } from 'components/automations/components/nodeTypes/stop';
import { AutomationNodeTagT } from 'components/automations/components/nodeTypes/tag';
import { AutomationNodeWaitT } from 'components/automations/components/nodeTypes/wait';
import type { NodeDataT, NodeT } from 'fable/flowEditor';

export interface AutomationActionT<TNodeType, TPayload>
  extends NodeT<TNodeType, TPayload> {
  connectionLabel: string;
  id: string;
  isAction: boolean;
  isEditable: boolean;
  isRemovable: boolean;
  nodeType: TNodeType;
  pageX: number;
  pageY: number;
  parentId: null | string;
  payload: TPayload;
}

export enum AutomationNodeTypes {
  INIT = 'INIT',
  QUEUE = 'QUEUE',
  CONDITION = 'CONDITION',
  TAG = 'TAG',
  REPLY = 'REPLY',
  WAIT = 'WAIT',
  STOP = 'STOP',
  CLOSE = 'CLOSE',
}

/**
 * AutomationFlowActionT Payload structures are still unknown Init-node is
 * required Minimum 2 nodes required
 */
export type AutomationFlowActionT =
  | AutomationNodeInitT
  | AutomationNodeQueueT
  | AutomationNodeConditionT
  | AutomationNodeTagT
  | AutomationNodeReplyT
  | AutomationNodeWaitT
  | AutomationNodeStopT
  | AutomationNodeCloseT;

export enum AutomationChannel {
  // TEXT
  WIDGETCHAT = 'widgetchat',
  CONTACTFORM = 'contact_form',
  EMAIL = 'email',
  FACEBOOKMESSENGER = 'facebookmessenger',
  WHATSAPP = 'whatsapp',
  // VOICE
  PSTNPHONE = 'pstnphone',
  VOICEMAIL = 'voicemail',
  CALLBACK = 'callback',
}
export enum AutomationChannelHumanNames {
  // TEXT
  widgetchat = 'Chat',
  contact_form = 'Contact form',
  email = 'Email',
  facebookmessenger = 'Facebook Messenger',
  whatsapp = 'WhatsApp',
  // VOICE
  pstnphone = 'Phone',
  voicemail = 'Voicemail',
  callback = 'Callback',
}

export enum AutomationTrigger {
  NONE = 'NONE',
  CONVERSATION_ASSIGNED = 'CONVERSATION_ASSIGNED',
  CONVERSATION_ABANDONED = 'CONVERSATION_ABANDONED',
  CONVERSATION_CLOSED = 'CONVERSATION_CLOSED',
  CONVERSATION_ADDED_TO_QUEUE = 'CONVERSATION_ADDED_TO_QUEUE',
  MESSAGE_SEND_BY_AGENT = 'MESSAGE_SEND_BY_AGENT',
  MESSAGE_SEND_BY_USER = 'MESSAGE_SEND_BY_USER',
}

export const AutomationTriggerHumanNames = {
  NONE: 'None',
  CONVERSATION_ASSIGNED: 'Conversation assigned to agent',
  CONVERSATION_ABANDONED: 'Conversation abandoned',
  CONVERSATION_CLOSED: 'Conversation closed',
  CONVERSATION_ADDED_TO_QUEUE: 'Conversation added to queue',
  MESSAGE_SEND_BY_AGENT: 'Message sent by agent',
  MESSAGE_SEND_BY_USER: 'Message sent by customer',
};

type AutomationUpdateHistoryItem = {
  readonly updated_by: string;
  readonly updated_at: string;
};

export interface AutomationT extends NodeDataT<Array<AutomationFlowActionT>> {
  id: string;
  name: string;
  description: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  channels: AutomationChannel[];
  trigger: AutomationTrigger;
  updated_by_history: AutomationUpdateHistoryItem[] | null;
}

export type AutomationListingT = Omit<AutomationT, 'actions' | 'channel'>;

export type AutomationCreatePayloadT = Omit<
  AutomationT,
  'id' | 'created_at' | 'updated_at'
>;
export type AutomationUpdatePayloadT = Partial<
  Pick<AutomationT, 'name' | 'description' | 'active' | 'channels' | 'actions'>
>;

export interface AutomationsQueryParamsT {
  search?: string;
  offset?: number;
  limit?: number;
  sort_by?: string;
}

export type AutomationsGetItemsResponseT = Response<{
  automations: AutomationListingT[];
}>;
